import type { ObjValues } from 'types/helpers';

/**
 * Used to send GoogleAnalytics events.
 *
 * Make sure you understand how Google Analytics works before adding new events/params.
 * When adding new events/params, Make sure they're not already covered by a built-in event,
 * an existing custom event, and that we actually need it.
 *
 * See the event objects next to this function definition for custom events.
 *
 * {@link https://support.google.com/analytics/answer/9267744?sjid=3946296830078935140-NC Event Quota}
 *
 * {@link https://support.google.com/analytics/answer/9234069?sjid=3946296830078935140-NC Automatically Collected Events}
 *
 * {@link https://support.google.com/analytics/answer/9216061?sjid=3946296830078935140-NC Enhances Measurement Events}
 *
 * {@link https://support.google.com/analytics/answer/9267735?sjid=3946296830078935140-NC Recommended Events}
 *
 * {@link https://support.google.com/analytics/answer/12229021?hl=en&ref_topic=13367566&sjid=3946296830078935140-NC Custom Events}
 *
 * {@link https://developers.google.com/tag-platform/gtagjs/reference/events Event Reference Table}
 *
 * @param eventName The name of the event, should only be from one of the lists below.
 * @param params Custom properties.
 * @param debug Log the event to the console instead of sending it to Google Analytics.
 */
export function trackEvent(
  eventName: GAEventName,
  params: Record<string, string>,
  debug = false
) {
  if (debug) {
    console.info('GA event:', eventName, params);
    return;
  }
  try {
    window.gtag('event', eventName, params);
  } catch (e) {
    console.warn('Error sending GA event', e);
  }
}

/**
 * Built-in events that maybe useful in this application. You can use these events without consuming quota.
 *
 * For more, see {@link https://support.google.com/analytics/answer/9267735?sjid=3946296830078935140-NC Recommended Events}
 */
export const BUILTIN_EVENTS = {
  JOIN_GROUP: 'join_group',
  LOGIN: 'login',
  SEARCH: 'search',
  SELECT_CONTENT: 'select_content',
} as const;

/**
 * We can only have 25 of these, so be sure we actually need it before adding a new one.
 */
export const CUSTOM_EVENTS = {
  API_ERROR: 'api_error',
  DASHBOARD_CTA: 'dashboard_cta',
  GLOBAL_CTA: 'global_cta',
} as const;

export type GAEventName =
  | ObjValues<typeof BUILTIN_EVENTS>
  | ObjValues<typeof CUSTOM_EVENTS>;
